import React, { useMemo, memo } from 'react';
import { FaChevronRight, FaTable, FaCalendarAlt } from 'react-icons/fa';
import PropTypes from 'prop-types';
import LogEventTable from './LogEventTable';

/**
 * LogFileRow Component
 * 
 * Displays a single log file with expandable content showing log events.
 * Optimized for performance with memoization and efficient re-rendering.
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.fileData - The log file data object
 * @param {boolean} props.expanded - Whether the row is expanded
 * @param {Function} props.onToggle - Function to call when toggling expansion
 * @param {boolean} props.showArchived - Whether to show archived logs
 * @param {number} props.lastUpdate - Timestamp of the last update to trigger re-renders
 */
const LogFileRow = memo(({ fileData, expanded, onToggle, showArchived, lastUpdate, filters, hasMatches }) => {
  /**
   * Format timestamp for display
   * 
   * @param {number} timestamp - Timestamp in milliseconds
   * @returns {string} - Formatted time string
   */
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    // For older dates, return the actual date
    return date.toLocaleString();
  };
  
  // Get the filename without the path
  const filename = useMemo(() => {
    const parts = fileData.file.split('/');
    return parts[parts.length - 1];
  }, [fileData.file]);
  

  
  // Filter events based on showArchived and search in message text
  const displayEvents = useMemo(() => {
    let filtered = showArchived ? fileData.events : fileData.events.filter(event => !event.archived);
    if (filters && filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.trim().toLowerCase();
      filtered = filtered.filter(event => event.message && event.message.toLowerCase().includes(searchTerm));
    }
    return filtered;
  }, [fileData.events, showArchived, filters]);
  
  // Get total event count
  const totalEvents = displayEvents.length;
  
  // Memoize the level counts to prevent unnecessary recalculations
  const levelCounts = useMemo(() => {
    const counts = {
      Debug: 0,
      Info: 0,
      Warning: 0,
      Error: 0,
      Critical: 0,
      SegmentFault: 0
    };
    
    fileData.events.forEach(event => {
      if (counts[event.level] !== undefined) {
        counts[event.level]++;
      }
    });
    
    return counts;
  }, [fileData.events]);

  return (
    <div className={`log-file-row ${expanded ? 'expanded' : ''} severity-${fileData.severity}`}>
      <div className="log-file-header" onClick={onToggle}>
        <div className="log-file-toggle">
          <FaChevronRight className={expanded ? 'expanded' : ''} />
        </div>
        
        <div className="log-file-label">{filename}</div>
        
        <div className="log-file-counts">
          <div className="log-file-count">
            <FaTable />
            <span>{totalEvents} events</span>
          </div>
          
          {Object.entries(levelCounts).filter(([_, count]) => count > 0).map(([level, count]) => (
            <div className={`log-file-count severity-${level}`} key={level}>
              <span>{level}: {count}</span>
            </div>
          ))}
          
          <div className="log-file-count">
            <FaCalendarAlt />
            <span>{formatTime(fileData.lastUpdated)}</span>
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="log-events-container">
          {!hasMatches && filters && (filters.search || filters.level.length > 0) && displayEvents.length === 0 && (
            <div className="log-file-no-matches">
              <p>No events match your current filters in this file. The table structure is shown for context.</p>
            </div>
          )}
          {/*
            Always render LogEventTable. If there are no events, the table will show an empty state row.
            This ensures the table headers and structure are always visible, even when filtered data is empty.
          */}
          <LogEventTable 
            events={displayEvents} 
            lastUpdate={lastUpdate}
            height={350}
            filters={filters}
            showEmptyMessage={!hasMatches && filters && (filters.search || filters.level.length > 0)}
          />
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Also check filters for shallow equality
  return (
    prevProps.expanded === nextProps.expanded &&
    prevProps.showArchived === nextProps.showArchived &&
    prevProps.lastUpdate === nextProps.lastUpdate &&
    prevProps.fileData.lastUpdated === nextProps.fileData.lastUpdated &&
    JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters)
  );
});

LogFileRow.propTypes = {
  fileData: PropTypes.shape({
    file: PropTypes.string.isRequired,
    events: PropTypes.array.isRequired,
    severity: PropTypes.string,
    health: PropTypes.string,
    lastUpdated: PropTypes.number
  }).isRequired,
  filters: PropTypes.object,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  showArchived: PropTypes.bool,
  lastUpdate: PropTypes.number
};

LogFileRow.defaultProps = {
  showArchived: false,
  lastUpdate: Date.now()
};

export default LogFileRow;
